import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Shield, ShoppingBag, Diamond, Gift } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { NotificationBell } from "@/components/NotificationBell";
import { useToast } from "@/hooks/use-toast";
import { NavLink } from "@/components/NavLink";

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [isSeller, setIsSeller] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    checkUser();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkUser();
    });
    return () => subscription.unsubscribe();
  }, []);

  const checkUser = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        if (userError.message !== "Auth session missing!") {
          console.error("Error fetching user:", userError);
        }
        setUser(null);
        setIsSeller(false);
        setIsAdmin(false);
        return;
      }

      setUser(user);
      
      if (user) {
        const [profileResponse, roleResponse] = await Promise.all([
          supabase.from("profiles").select("user_type").eq("user_id", user.id).single(),
          supabase.from("user_roles").select("role").eq("user_id", user.id).eq("role", "admin").maybeSingle()
        ]);

        if (profileResponse.error) {
          console.error("Error fetching profile:", profileResponse.error);
        }
        if (roleResponse.error) {
          console.error("Error fetching role:", roleResponse.error);
        }

        setIsSeller(profileResponse.data?.user_type === "seller");
        setIsAdmin(!!roleResponse.data);
      } else {
        setIsSeller(false);
        setIsAdmin(false);
      }
    } catch (error) {
      console.error("Error in checkUser:", error);
      setUser(null);
      setIsSeller(false);
      setIsAdmin(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const handleUploadArtwork = () => {
    if (!isSeller) {
      toast({
        title: "Seller Account Required",
        description: "You need to become a Seller to upload artwork",
        variant: "destructive",
      });
      return;
    }
    navigate("/dashboard");
  };

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/gallery", label: "Gallery" },
    { to: "/marketplace", label: "Marketplace" },
    { to: "/favorites", label: "Favorites" },
    { to: "/art-analysis", label: "Analysis" },
    { to: "/search", label: "Search" },
    { to: "/art-auction", label: "Auctions" },
    { to: "/art-news", label: "News" },
    { to: "/forum", label: "Forum" },
    { to: "/network", label: "Network" },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled 
        ? 'bg-background/95 backdrop-blur-xl shadow-soft border-b border-border/50' 
        : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-4 py-3 md:py-4">
        <div className="flex items-center justify-between h-20 md:h-14">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
            <span className="text-2xl font-display font-semibold luxury-text tracking-wide">
              Art Experto
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1 flex-wrap justify-center flex-1 px-8">
            {navLinks.map((link) => (
              <NavLink key={link.to} to={link.to}>
                {link.label}
              </NavLink>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-3">
            {user && <NotificationBell />}
            {user && (
              <Button 
                onClick={handleUploadArtwork} 
                variant="ghost" 
                size="sm"
                className="text-xs tracking-wide hover:text-primary"
              >
                Upload
              </Button>
            )}
            {user && !isSeller && (
              <Button 
                onClick={() => navigate("/buyer-dashboard")} 
                variant="ghost" 
                size="sm"
                className="text-xs tracking-wide"
              >
                <ShoppingBag className="h-4 w-4 mr-1" />
                Dashboard
              </Button>
            )}
            {isSeller && (
              <Button 
                onClick={() => navigate("/my-artworks")} 
                variant="ghost" 
                size="sm"
                className="text-xs tracking-wide"
              >
                My Artworks
              </Button>
            )}
            {user && (
              <Button 
                onClick={() => navigate("/referrals")} 
                variant="ghost" 
                size="sm"
                className="text-xs tracking-wide"
              >
                <Gift className="h-4 w-4 mr-1" />
                Referrals
              </Button>
            )}
            {isAdmin && (
              <Button 
                onClick={() => navigate("/admin")} 
                variant="ghost" 
                size="sm"
                className="text-primary text-xs tracking-wide"
              >
                <Shield className="h-4 w-4 mr-1" />
                Admin
              </Button>
            )}
            {user ? (
              <Button 
                onClick={handleLogout} 
                variant="outline" 
                size="sm"
                className="text-xs tracking-wide luxury-border"
              >
                Sign out
              </Button>
            ) : (
              <Button 
                onClick={() => navigate("/auth")} 
                size="sm"
                className="bg-gradient-hero text-primary-foreground shadow-gold hover:shadow-glow text-xs tracking-wide transition-all duration-500"
              >
                <Diamond className="h-3 w-3 mr-1" />
                Sign in
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
            className="lg:hidden p-2 text-foreground hover:text-primary transition-colors duration-300"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-6 space-y-2 border-t border-border/50 mt-4 animate-fade-in">
            {navLinks.map((link) => (
              <Link 
                key={link.to}
                to={link.to} 
                className="block py-3 px-4 text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-300 rounded-lg font-medium" 
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <Link 
              to="/profile" 
              className="block py-3 px-4 text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-300 rounded-lg font-medium" 
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Profile
            </Link>
            
            <div className="pt-4 space-y-3 border-t border-border/50 mt-4">
              {user ? (
                <>
                  <Button 
                    onClick={() => { handleUploadArtwork(); setIsMobileMenuOpen(false); }} 
                    variant="outline" 
                    className="w-full luxury-border"
                  >
                    Upload Artwork
                  </Button>
                  {!isSeller && (
                    <Button 
                      onClick={() => { navigate("/buyer-dashboard"); setIsMobileMenuOpen(false); }} 
                      variant="outline" 
                      className="w-full luxury-border"
                    >
                      <ShoppingBag className="h-4 w-4 mr-2" />
                      My Dashboard
                    </Button>
                  )}
                  {isSeller && (
                    <Button 
                      onClick={() => { navigate("/my-artworks"); setIsMobileMenuOpen(false); }} 
                      variant="outline" 
                      className="w-full luxury-border"
                    >
                      My Artworks
                    </Button>
                  )}
                  {isAdmin && (
                    <Button 
                      onClick={() => { navigate("/admin"); setIsMobileMenuOpen(false); }} 
                      className="w-full bg-gradient-hero text-primary-foreground"
                    >
                      <Shield className="h-4 w-4 mr-2" />
                      Admin Panel
                    </Button>
                  )}
                  <Button 
                    onClick={() => { navigate("/referrals"); setIsMobileMenuOpen(false); }} 
                    variant="outline" 
                    className="w-full luxury-border"
                  >
                    <Gift className="h-4 w-4 mr-2" />
                    Referral Program
                  </Button>
                  <Button 
                    onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }} 
                    variant="outline" 
                    className="w-full"
                  >
                    Sign out
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={() => { navigate("/auth"); setIsMobileMenuOpen(false); }} 
                  className="w-full bg-gradient-hero text-primary-foreground shadow-gold"
                >
                  <Diamond className="h-4 w-4 mr-2" />
                  Sign in
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;